import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBlockUser } from "@/hooks/useBlockUser";
import { toast } from "@/hooks/useToast";
import { useUnblockUser } from "@/hooks/useUnblockUser";

interface UseUserBlockActionsOptions {
  onBlockedChange?: () => Promise<unknown> | unknown;
  invalidateUserInfo?: boolean;
}

export const useUserBlockActions = ({
  onBlockedChange,
  invalidateUserInfo = false,
}: UseUserBlockActionsOptions = {}) => {
  const { t } = useTranslation();
  const { blockUser, isBlocking } = useBlockUser({ invalidateUserInfo });
  const { unblockUser, isUnblocking } = useUnblockUser({ invalidateUserInfo });

  const runAfterChange = useCallback(async () => {
    if (onBlockedChange) {
      await onBlockedChange();
    }
  }, [onBlockedChange]);

  const blockUserWithFeedback = useCallback(
    async (userId: number) => {
      await blockUser(userId);
      toast({
        title: t("common.userBlockSuccess", "User blocked"),
        description: t(
          "common.userBlockSuccessMessage",
          "You have blocked this user.",
        ),
        variant: "success",
      });
      await runAfterChange();
    },
    [blockUser, runAfterChange, t],
  );

  const unblockUserWithFeedback = useCallback(
    async (userId: number) => {
      await unblockUser(userId);
      toast({
        title: t("common.userUnblockSuccess", "User unblocked"),
        description: t(
          "common.userUnblockSuccessMessage",
          "You have unblocked this user.",
        ),
        variant: "success",
      });
      await runAfterChange();
    },
    [runAfterChange, t, unblockUser],
  );

  return {
    blockUserWithFeedback,
    unblockUserWithFeedback,
    isBlocking,
    isUnblocking,
    isPending: isBlocking || isUnblocking,
  };
};
